<div class="report-container">
  <div class="report-header">
    <div class="title-section">
      <h2 class="modern-title">Balance Sheet</h2>
      <p class="modern-subtitle">Financial position overview</p>
    </div>

    <div class="controls-section">
      <div class="btn-group modern-btn-group">
        @for (p of periods; track p) {
          <button
            class="btn modern-filter-btn"
            [class.active]="period === p"
            (click)="setPeriod(p)">
            {{ p }}
          </button>
        }
      </div>

      @if (period === 'Custom') {
        <div class="date-picker-wrapper ms-3 d-flex align-items-center">
          <div class="position-relative">
            <input
              #dp="bsDatepicker"
              type="text"
              class="date-input"
              [(ngModel)]="asOfDateInput"
              [bsConfig]="bsConfig"
              bsDatepicker
              (bsValueChange)="onDateChange()"
              style="width: 130px;"
              />
            </div>
          </div>
        }

        <button class="btn modern-action-btn primary-btn ms-3" (click)="exportPdf()">
          Export PDF
        </button>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="summary-cards">
      <div class="summary-card glass-card">
        <div class="card-icon asset-icon">
        </div>
        <div class="card-details">
          <div class="card-period">{{ period }}</div>
          <div class="card-label">Total Assets</div>
          <div class="card-value text-asset">{{ totalAssets | currency }}</div>
        </div>
      </div>
      <div class="summary-card glass-card">
        <div class="card-icon liability-icon">
        </div>
        <div class="card-details">
          <div class="card-period">{{ period }}</div>
          <div class="card-label">Total Liabilities</div>
          <div class="card-value text-liability">
            {{ totalLiabilities | currency }}
          </div>
        </div>
      </div>
      <div class="summary-card glass-card">
        <div class="card-icon equity-icon">
        </div>
        <div class="card-details">
          <div class="card-period">{{ period }}</div>
          <div class="card-label">Total Equity</div>
          <div class="card-value text-equity">{{ totalEquity | currency }}</div>
        </div>
      </div>
    </div>

    <div class="chart-section glass-card mt-4">
      <div
        class="section-header d-flex justify-content-between align-items-center mb-3"
        >
        <div class="d-flex align-items-center">
          <h3 class="section-title">Financial Position</h3>
          <span class="ms-2 period-tag">{{ period }}</span>
        </div>
        <button
          class="btn btn-link text-white p-0"
          (click)="isChartCollapsed = !isChartCollapsed"
          >
        </button>
      </div>
      <div [collapse]="isChartCollapsed" [isAnimated]="true">
        <div class="chart-wrapper">
          <div class="row h-100 g-0">
            <!-- Overview -->
            <div class="col-md-4 border-end border-white border-opacity-10">
              @if (chartOptions) {
                <ag-charts [options]="chartOptions" style="height: 100%"></ag-charts>
              }
            </div>

            <!-- Asset Composition -->
            <div class="col-md-4 border-end border-white border-opacity-10">
              @if (assetChartOptions) {
                <ag-charts [options]="assetChartOptions" style="height: 100%"></ag-charts>
              }
            </div>

            <!-- Current Ratio Gauge -->
            <div class="col-md-4 position-relative">
              @if (gaugeChartOptions) {
                <ag-charts [options]="gaugeChartOptions" style="height: 100%"></ag-charts>
              }
              <div class="position-absolute top-50 start-50 translate-middle text-center mt-3">
                <div class="h3 fw-medium mb-0" [style.color]="currentRatio >= 1.5 ? '#00a8ff' : '#ff4d4d'">
                  {{ currentRatio | number:'1.2-2' }}
                </div>
                <div class="small text-white text-opacity-50">Ratio</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Detailed Breakdown -->
    <div class="row mt-4 mb-4">
      <!-- Assets Column -->
      <div class="col-md-6">
        <div class="breakdown-card glass-card">
          <div
            class="section-header d-flex justify-content-between align-items-center mb-3"
            (click)="isAssetsCollapsed = !isAssetsCollapsed"
            style="cursor: pointer"
            >
            <div class="d-flex align-items-center gap-3">
              <h3 class="section-title text-asset mb-0">Assets</h3>
              <span class="ms-2 period-tag">{{ period }}</span>
              <span class="badge asset-badge">{{ totalAssets | currency }}</span>
            </div>
            <button class="btn btn-link text-white p-0">
            </button>
          </div>

          <div [collapse]="isAssetsCollapsed" [isAnimated]="true">
            @for (group of assetAccounts; track group) {
              <h5 class="group-title mt-4">{{ group.category }}</h5>
              <div class="table-responsive">
                <table class="table modern-table">
                  <tbody>
                    @for (acc of group.items; track acc) {
                      <tr>
                        <td style="color: white">{{ acc.name }}</td>
                        <td class="text-end fw-medium" style="color: white">
                          {{ acc.amount | currency }}
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Liabilities & Equity Column -->
      <div class="col-md-6 mt-4 mt-md-0">
        <div class="breakdown-card glass-card">
          <div
            class="section-header d-flex justify-content-between align-items-center mb-3"
            (click)="isLiabilitiesCollapsed = !isLiabilitiesCollapsed"
            style="cursor: pointer"
            >
            <div class="d-flex align-items-center gap-3">
              <h3 class="section-title text-liability mb-0">Liabilities & Equity</h3>
              <span class="ms-2 period-tag">{{ period }}</span>
              <span class="badge liability-badge">{{ (totalLiabilities + totalEquity) | currency }}</span>
            </div>
            <button class="btn btn-link text-white p-0">
            </button>
          </div>

          <div [collapse]="isLiabilitiesCollapsed" [isAnimated]="true">
            @for (group of liabilityAccounts; track group) {
              <h5 class="group-title mt-4">{{ group.category }}</h5>
              <div class="table-responsive">
                <table class="table modern-table">
                  <tbody>
                    @for (acc of group.items; track acc) {
                      <tr>
                        <td style="color: white">{{ acc.name }}</td>
                        <td class="text-end fw-medium" style="color: white">
                          {{ acc.amount | currency }}
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }

            <h5 class="group-title mt-4">Equity</h5>
            <div class="table-responsive">
              <table class="table modern-table">
                <tbody>
                  @for (acc of equityAccounts; track acc) {
                    <tr>
                      <td style="color: white">{{ acc.name }}</td>
                      <td class="text-end fw-medium" style="color: white">
                        {{ acc.amount | currency }}
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
